import MapboxSDK from '@mapbox/mapbox-sdk';
import MapboxGeocoding from '@mapbox/mapbox-sdk/services/geocoding';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';

import { APIConfig } from 'src/config';
import getFullAddressString from 'src/util/address';
import logger from 'src/util/logger';

// PropertyListMap controls are hidden via global stylesheet on the mapbox component
class PropertyListMap extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      render: false,
      gps: {
        latitude: null,
        longitude: null,
        zoom: null,
      },
    };
  }

  componentDidMount() {
    this.focus();
  }

  componentWillUnmount() {
    if (this.map) { this.map.remove(); }
  }

  focus = () => {
    // Maps are optional; neither geocoding nor static images work without a token.
    if (!APIConfig().MAPBOX_TOKEN?.trim()) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-restricted-properties -- Dev-only optional-map diagnostic.
        logger.debug('[PropertyListMap] Skipping optional map: MAPBOX_TOKEN is not configured in development.');
      }
      return;
    }

    const { address, zoom } = this.props;
    const { gps } = address;

    if (gps) {
      const { latitude, longitude } = gps;

      this.setState((prevState) => ({
        ...prevState,
        render: true,
        gps: {
          latitude,
          longitude,
          zoom,
        },
      }));

      return;
    }

    // Include country in the geocoding query to improve result accuracy. Without it,
    // addresses like "Melbourne, VIC" could match locations in the US instead of Australia,
    // since Mapbox's forward geocoding API searches globally and returns the best match.
    const query = getFullAddressString(address, { includeCountry: true });
    if (!query) {
      return;
    }

    const mbxClient = MapboxSDK({ accessToken: APIConfig().MAPBOX_TOKEN });
    const geocodingClient = MapboxGeocoding(mbxClient);

    geocodingClient.forwardGeocode({ query, limit: 1 })
      .send()
      .then((response) => {
        if (response && response.body && response.body.features && response.body.features.length) {
          const feature = response.body.features[0];

          this.setState((prevState) => ({
            ...prevState,
            render: true,
            gps: {
              latitude: feature.center[1],
              longitude: feature.center[0],
              zoom,
            },
          }));
        }
      });
  };

  image = () => {
    const { gps, render } = this.state;
    if (!render) { return null; }

    const { latitude, longitude, zoom } = gps;
    const {
      address, height, intl, width,
    } = this.props;

    const addressLabel = getFullAddressString(address) ?? '';
    const altDesc = intl.formatMessage({ id: 'property.property_list.map_of.alt_text', defaultMessage: 'Map of {location}' }, { location: addressLabel });

    const src = `https://api.mapbox.com/styles/v1/joel-enosi/cl79xxw5z000014qx5eyk1b6m/static/pin-l+3bb667(${longitude},${latitude})/${longitude},${latitude},${zoom},0/${width}x${height}?access_token=${APIConfig().MAPBOX_TOKEN}`;

    return (
      <img
        src={src}
        alt={altDesc}
        width={width}
        height={height}
        style={{ width: 'auto', maxWidth: '100%', objectFit: 'cover' }}
      />
    );
  };

  render() {
    return this.image();
  }
}

PropertyListMap.propTypes = {
  address: PropTypes.shape({
    line1: PropTypes.string,
    line2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postcode: PropTypes.string,
    gps: PropTypes.shape({
      latitude: PropTypes.number.isRequired,
      longitude: PropTypes.number.isRequired,
    }),
  }),
  zoom: PropTypes.number,
  width: PropTypes.string,
  height: PropTypes.string,
  intl: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

PropertyListMap.defaultProps = {
  address: null,
  height: '200',
  width: '400',
  zoom: 12,
};

export default injectIntl(PropertyListMap);
